// Pure list logic for the desktop Sidebar (JW2). Kept framework-free so the
// merge/sort/badge/search rules are unit-tested without mounting React — the
// component is a thin renderer over `buildSidebar`.
//
// No new data paths: the inputs are exactly what MainScreen already derives
// (cached friends, cached blocks, device-local favorites + call log, live
// presence). This module only reshapes them into the three sidebar sections.

import type { Friend, PresenceStatus } from '@merkury/protocol';
import type { StoredCallLogEntry } from '../../storage/kvStores.js';

// Presence tiers, best first (docs/06 §5): in the app (green) → reachable
// (yellow) → offline (gray). Drives the Contacts section order.
const PRESENCE_RANK: Record<PresenceStatus, number> = { app: 0, reachable: 1, offline: 2 };

/** Case-insensitive, whitespace-trimmed substring match; empty query matches all. */
export function nameMatches(query: string, name: string): boolean {
  const q = query.trim().toLowerCase();
  return q === '' || name.toLowerCase().includes(q);
}

/**
 * Favorites: starred friends in the user's own star order, minus anyone
 * blocked or no longer a friend (same rule as MainScreen's `favoriteFriends`).
 */
export function favoriteFriends(
  favoriteIds: readonly string[],
  friends: readonly Friend[],
  blocked: readonly string[],
): Friend[] {
  const isBlocked = new Set(blocked);
  return favoriteIds
    .filter((id) => !isBlocked.has(id))
    .map((id) => friends.find((f) => f.userId === id))
    .filter((f): f is Friend => f !== undefined);
}

/**
 * Contacts section: every friend that isn't already a favorite, sorted by
 * presence tier and stable within a tier (input order preserved), so the list
 * doesn't reshuffle on equal-presence contacts.
 */
export function contactFriends(
  friends: readonly Friend[],
  favoriteIds: readonly string[],
  presenceFor: (friend: Friend) => PresenceStatus,
): Friend[] {
  const isFavorite = new Set(favoriteIds);
  return friends
    .filter((f) => !isFavorite.has(f.userId))
    .map((friend, index) => ({ friend, index }))
    .sort((a, b) => {
      const byTier = PRESENCE_RANK[presenceFor(a.friend)] - PRESENCE_RANK[presenceFor(b.friend)];
      return byTier !== 0 ? byTier : a.index - b.index;
    })
    .map((x) => x.friend);
}

/** Unseen incoming-missed count — the Recents badge (same set as MainScreen). */
export function missedUnseenCount(calls: readonly StoredCallLogEntry[]): number {
  return calls.filter((c) => c.outcome === 'missed' && !c.seen).length;
}

export interface SidebarSections {
  favorites: Friend[];
  contacts: Friend[];
  recents: StoredCallLogEntry[];
  /** Unseen-missed count over the FULL log — the search query never hides it. */
  missedUnseen: number;
  /** True when the query filtered every section empty (drives "No matches"). */
  noRows: boolean;
}

export interface BuildSidebarArgs {
  friends: readonly Friend[];
  favoriteIds: readonly string[];
  blocked: readonly string[];
  calls: readonly StoredCallLogEntry[];
  query: string;
  presenceFor: (friend: Friend) => PresenceStatus;
  /** Resolve a recents entry to a display name (peerName/friends/unknown). */
  nameOf: (entry: StoredCallLogEntry) => string;
  /** Device-local alias map (docs/08 §4): search matches alias OR real name. */
  aliases?: Readonly<Record<string, string>>;
}

/** A friend matches a query on either their alias or their real display name (docs/08 §4). */
function friendMatches(
  query: string,
  friend: Friend,
  aliases: Readonly<Record<string, string>>,
): boolean {
  const alias = aliases[friend.userId];
  return nameMatches(query, friend.displayName) || (!!alias && nameMatches(query, alias));
}

/**
 * Build the three sections in render order, applying the search filter by name
 * (contacts by alias OR `displayName`, recents by their resolved — already
 * alias-aware — name). The badge count stays computed from the unfiltered log
 * so searching never changes it.
 */
export function buildSidebar(args: BuildSidebarArgs): SidebarSections {
  const { friends, favoriteIds, blocked, calls, query, presenceFor, nameOf, aliases = {} } = args;

  const favorites = favoriteFriends(favoriteIds, friends, blocked).filter((f) =>
    friendMatches(query, f, aliases),
  );
  const contacts = contactFriends(friends, favoriteIds, presenceFor).filter((f) =>
    friendMatches(query, f, aliases),
  );
  const recents = calls.filter((entry) => nameMatches(query, nameOf(entry)));

  return {
    favorites,
    contacts,
    recents,
    missedUnseen: missedUnseenCount(calls),
    noRows: favorites.length === 0 && contacts.length === 0 && recents.length === 0,
  };
}
